import { useCallback, useReducer } from "react";
import { Box } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import debug from "src/modules/debug-logger";
import Dialog from "src/components/Dialog";
import GenericFailureDialog from "src/components/GenericFailureDialog";
import lullabyFiles from "src/modules/lullaby/lullaby-files";
import useAudioController from "src/hooks/useAudioController";
import ScrollBox from "src/atoms/ScrollBox";
import NavigationBar from "src/components/NavigationBar";
import LullabiesList from "src/modules/lullaby/components/LullabiesList";
import { usePurchaseContext } from "src/modules/purchase/PurchaseContext";

import AddLullabyMenu from "./AddLullabyMenu";
import { removeLullaby } from "src/modules/lullaby/ajax/cloud";
import { deleteLullaby } from "src/modules/lullaby/duck/actions";
import { isFreemium } from "src/modules/auth/duck/selectors";
import { useApp } from "src/components/App/Context";

const initialDeleteState = { isDeleting: false, hasError: false };
function deleteReducer(state, action) {
  switch (action.type) {
    case "REQUEST":
      return { isDeleting: true, hasError: false };
    case "ERROR":
      return { isDeleting: false, hasError: true };
    case "RESET":
      return initialDeleteState;
    default:
      throw new Error("Unknown action type.");
  }
}

export default function Lullabies({
  lullabies,
  openEditor,
  openLibrary,
  onBack,
  onClose
}) {
  const { t } = useTranslation();
  const purchase = usePurchaseContext();

  const dispatch = useDispatch();
  const isFreeVersion = useSelector(isFreemium);
  const app = useApp();
  const [deleteState, deleteDispatch] = useReducer(
    deleteReducer,
    initialDeleteState
  );
  const audioController = useAudioController({
    srcResolver: lullabyFiles.srcResolver
  });

  const deleteLullabyHandler = useCallback(
    async (lullabyId) => {
      try {
        deleteDispatch({ type: "REQUEST" });
        await removeLullaby(lullabyId);
        dispatch(deleteLullaby(lullabyId));
        app.synchronizer.announceItemChange(
          app.synchronizer.SynchronizableItems.LULLABIES
        );
        if (lullabies[lullabyId]?.audioFile) {
          lullabyFiles.delete(lullabies[lullabyId].audioFile.name);
        }
        deleteDispatch({ type: "RESET" });
      } catch (error) {
        debug.lullaby("deleteLullabyHandler failed", error);
        deleteDispatch({ type: "ERROR" });
      }
    },
    [dispatch, app.synchronizer, lullabies]
  );

  return (
    <>
      <GenericFailureDialog
        isOpen={audioController.hasPlayError || deleteState.hasError}
        onDismiss={
          audioController.hasPlayError
            ? audioController.clearPlayError
            : () => deleteDispatch({ type: "RESET" })
        }
      />
      <Dialog
        isOpen={deleteState.isDeleting}
        isProcessing={true}
        processingText={t("deleting")}
      />
      <NavigationBar
        headingKey="lullaby.manageHeader"
        actionButton={{
          Menu: (props) => (
            <AddLullabyMenu
              openLibrary={openLibrary}
              openEditor={() =>
                isFreeVersion
                  ? purchase.open({ content: "playAndRecord" })
                  : openEditor()
              }
              {...props}
            />
          ),
          labelKey: "lullaby.addHeader"
        }}
        onBack={onBack}
        onClose={onClose}
      />
      <Box py="16px">
        <ScrollBox height="calc(100% - 61px)" px="24px">
          <LullabiesList
            lullabies={lullabies}
            audioController={audioController}
            onDelete={deleteLullabyHandler}
            onEdit={openEditor}
          />
        </ScrollBox>
      </Box>
    </>
  );
}
